import React, { Fragment, useCallback, useMemo } from 'react'
import Icon from 'components/Icon/Icon'
import Divider from 'components/Shared/Divider'
import InputField from 'components/Shared/Fields'
import SetValue from 'components/Shared/Inputs/SetValueInput'
import Text from 'particles/Text'
import { useFormikContext } from 'formik'
import _ from 'lodash'
import { useNewTranslation } from 'hooks'
import { useSelector } from 'react-redux'
import { Box } from 'rebass'
import { CountrySettings } from 'services/api/constants'
import { generateUuid } from 'services/uuid'
import { selectCampaign } from 'state'
import { CAMPAIGN_STATUS_ENUM } from 'state/campaigns'
import { selectCountrySetting } from 'state/constants'
import styled from 'particles/themes/styled'
import { theme } from 'theme'
import {
    AdditionPayoutCardContainer,
    BaseEditorContainer,
    PayoutSectionCardContainer,
} from '../PredefinedTaskEditor/styles'
import { EditorValues } from './types'
import CampaignEditorInputFieldContainer from 'components/Shared/Permission/CampaignEditorInputFieldContainer'
import { EngagementId, EngagementIds } from 'state/tasks'
import { getEngagementTranslation, getEngagementPayoutTranslation } from 'components/utils/translationsMap'
import { SelectedEngagementConfig } from '../PredefinedTaskEditor/PayoutEditor'
import RectangleButton from 'particles/Button/RectangleButton'
import Toggle from 'particles/Toggle'
import { fieldIdentifiers } from 'components/Shared/Comment'
import { snakeCaseToCamelCase } from 'components/utils'

const EditorContainer = styled(BaseEditorContainer)`
    min-height: 100%;
    height: fit-content;
    box-sizing: border-box;

    & > .payout-section {
        max-width: 594px;
    }
`
interface CustomPayoutEditorProps {
    campaignUuid: string
    taskUuid: string
}
const CustomPayoutEditor: React.FC<CustomPayoutEditorProps> = ({ campaignUuid, taskUuid }) => {
    const [t] = useNewTranslation()
    const campaign = useSelector(selectCampaign(campaignUuid))
    const countrySetting = useSelector(selectCountrySetting(campaign.brand.country)) as CountrySettings | undefined
    const { values: formikValues, setFieldValue } = useFormikContext<EditorValues>()
    const isCampaignLive = Boolean(campaign && campaign.status === CAMPAIGN_STATUS_ENUM.LIVE)

    const budgetCurrencySign = useMemo(() => {
        if (!countrySetting) return null
        return countrySetting.currency.sign
    }, [countrySetting])

    const payoutExtras = formikValues.payouts.extras || {}

    const availableEngagementMetrics = useMemo<string[]>(
        () => formikValues.payouts.engagementMetrics.map((metric: SelectedEngagementConfig) => metric.value),
        [formikValues.payouts],
    )

    const toggleEngagementMetric = (payoutMetric: string, isToggled: boolean): void => {
        setFieldValue(`payouts.${payoutMetric}`, {
            ...formikValues.payouts[payoutMetric],
            cost: isToggled ? 1 : 0,
            min: isToggled ? 1 : 0,
            max: isToggled ? 1 : 0,
        })
    }

    const handleAddExtraPayout = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault()
        const uuid = generateUuid()
        setFieldValue(`payouts.extras.${uuid}`, {
            uuid,
            title: null,
            description: null,
            cost: null,
            min: null,
            max: null,
        })
    }, [])

    return (
        <EditorContainer>
            <Text scale="H4" className="title" color="primary">
                {t('campaignEditor.createTask.payoutPageTitle')}
            </Text>

            <Divider maxWidth="1006px" className="first-divider" />

            <div className="payout-section">
                <CampaignEditorInputFieldContainer>
                    <InputField.NumberInputField
                        title={t('campaignEditor.createTask.enterFixedPayoutHeader')}
                        subtitle={t('campaignEditor.createTask.addFixedPayoutSubtext')}
                        currencySign={budgetCurrencySign}
                        country={campaign.brand.country}
                        name={`payouts.base.cost`}
                        decimal
                        isRequired
                        placeholder={'0.00'}
                        identifier={`${taskUuid}_${fieldIdentifiers.payoutBase}`}
                    />
                </CampaignEditorInputFieldContainer>
                <Divider className="divider" />
            </div>

            {Boolean(availableEngagementMetrics.length) && (
                <div className="payout-section">
                    <Text scale="bodyM700" color="primary">
                        {t('campaignEditor.createTask.enterPayoutEngagementHeader')}
                    </Text>
                    <Text scale="bodyS400" color="secondary">
                        {t('campaignEditor.createTask.enterPayoutEngagementSelectedCustomTaskSubtext')}
                    </Text>
                    <Box height={theme.newSpacing.l} />
                    {availableEngagementMetrics.map(payoutMetric => {
                        const payoutHasValue = Object.values(formikValues.payouts[payoutMetric]).some(
                            val => Number(val) > 0,
                        )
                        const engagementLabel = EngagementIds.includes(payoutMetric as EngagementId)
                            ? getEngagementPayoutTranslation[payoutMetric as EngagementId]?.(t)
                            : { perLabel: '', minLabel: '', maxLabel: '' }
                        const payoutTypeIdentifier = snakeCaseToCamelCase(payoutMetric)
                        return (
                            <Fragment key={payoutMetric}>
                                <PayoutSectionCardContainer key={payoutMetric}>
                                    <CampaignEditorInputFieldContainer>
                                        <Toggle
                                            isDisabled={isCampaignLive}
                                            isChecked={payoutHasValue}
                                            handleChange={(): void => {
                                                const updateValue = !Boolean(payoutHasValue)
                                                toggleEngagementMetric(payoutMetric, updateValue)
                                            }}
                                            isReadOnly={isCampaignLive ? isCampaignLive : undefined}
                                            label={getEngagementTranslation[payoutMetric as EngagementId](t)}
                                            identifier={`${taskUuid}_${payoutTypeIdentifier}_${fieldIdentifiers.engagementToggle}`}
                                        />
                                    </CampaignEditorInputFieldContainer>
                                    {payoutHasValue ? (
                                        <CampaignEditorInputFieldContainer>
                                            <InputField.NumberInputField
                                                currencySign={budgetCurrencySign}
                                                country={campaign.brand.country}
                                                name={`payouts.${payoutMetric}.cost`}
                                                title={engagementLabel?.perLabel}
                                                isRequired
                                                decimal
                                                placeholder={'0.00'}
                                                identifier={`${taskUuid}_${payoutTypeIdentifier}_${fieldIdentifiers.engagementCost}`}
                                            />
                                            <InputField.NumberInputField
                                                name={`payouts.${payoutMetric}.min`}
                                                country={campaign.brand.country}
                                                title={engagementLabel?.minLabel}
                                                isRequired
                                                placeholder={'0'}
                                                identifier={`${taskUuid}_${payoutTypeIdentifier}_${fieldIdentifiers.engagementMin}`}
                                            />
                                            <InputField.NumberInputField
                                                name={`payouts.${payoutMetric}.max`}
                                                title={engagementLabel?.maxLabel}
                                                country={campaign.brand.country}
                                                isRequired
                                                placeholder={'0'}
                                                identifier={`${taskUuid}_${payoutTypeIdentifier}_${fieldIdentifiers.engagementMax}`}
                                            />
                                        </CampaignEditorInputFieldContainer>
                                    ) : null}
                                </PayoutSectionCardContainer>
                                <Divider className="divider" />
                            </Fragment>
                        )
                    })}
                </div>
            )}

            <div className="payout-section">
                <Text scale="bodyS500" color="primary" className="other-payout-title">
                    {t('campaignEditor.createTask.addOtherPayoutHeader')}
                </Text>
                <Text scale="bodyS400" color="secondary" className="addition-sub-title">
                    {t('campaignEditor.createTask.addOtherPayoutSubtext')}
                </Text>
                <div>
                    {payoutExtras &&
                        Object.keys(payoutExtras).map((id: string) => {
                            return (
                                <AdditionPayoutCardContainer
                                    id={`payout-extra-${id}`}
                                    key={id}
                                    className="addition-block"
                                >
                                    <div className="field-title">
                                        <Text scale="bodyM700" color="primary">
                                            {t('campaignEditor.createTask.addOtherPayoutHeader')}
                                        </Text>
                                        {!isCampaignLive && (
                                            <Icon.CloseIcon
                                                onClick={(): void => {
                                                    setFieldValue(`payouts.extras.${id}`, undefined)
                                                }}
                                            />
                                        )}
                                    </div>
                                    <SetValue name={`payouts.extras.${id}.uuid`} value={id} />
                                    <CampaignEditorInputFieldContainer>
                                        <InputField.TextInputField
                                            label={t('campaignEditor.createTask.enterTitleHeader')}
                                            name={`payouts.extras.${id}.title`}
                                            width="100%"
                                            isRequired
                                            identifier={`${id}_${fieldIdentifiers.extraPayoutTitle}`}
                                        />
                                        <InputField.TextAreaField
                                            label={t('campaignEditor.createTask.enterDescriptionHeader')}
                                            name={`payouts.extras.${id}.description`}
                                            width="100%"
                                            isRequired
                                            identifier={`${id}_${fieldIdentifiers.extraPayoutDescription}`}
                                        />
                                        <InputField.NumberInputField
                                            title={t('campaignEditor.createTask.enterPayoutForEachHeader')}
                                            subtitle={t('campaignEditor.createTask.enterPayoutForEachSubtext')}
                                            currencySign={budgetCurrencySign}
                                            country={campaign.brand.country}
                                            name={`payouts.extras.${id}.cost`}
                                            isRequired
                                            decimal
                                            placeholder={'0.00'}
                                            identifier={`${id}_${fieldIdentifiers.extraPayoutCost}`}
                                        />
                                        <InputField.NumberInputField
                                            title={t('campaignEditor.createTask.enterMinimumOtherPayout')}
                                            name={`payouts.extras.${id}.min`}
                                            country={campaign.brand.country}
                                            isRequired
                                            placeholder={'0'}
                                            identifier={`${id}_${fieldIdentifiers.extraPayoutMin}`}
                                        />
                                        <InputField.NumberInputField
                                            title={t('campaignEditor.createTask.enterMaximumOtherPayout')}
                                            name={`payouts.extras.${id}.max`}
                                            country={campaign.brand.country}
                                            isRequired
                                            placeholder={'0'}
                                            identifier={`${id}_${fieldIdentifiers.extraPayoutMax}`}
                                        />
                                    </CampaignEditorInputFieldContainer>
                                </AdditionPayoutCardContainer>
                            )
                        })}
                    {
                        <CampaignEditorInputFieldContainer>
                            {(isReadOnly: boolean) =>
                                !isReadOnly &&
                                !isCampaignLive && (
                                    <div style={{ marginTop: theme.newSpacing.s }}>
                                        <RectangleButton
                                            form="outlined"
                                            size="s"
                                            label={t('button.addPayout')}
                                            onClick={handleAddExtraPayout}
                                            icon="plus"
                                        />
                                    </div>
                                )
                            }
                        </CampaignEditorInputFieldContainer>
                    }
                </div>
            </div>
        </EditorContainer>
    )
}

export default CustomPayoutEditor
